/**
 * Queries do CHAT (estilo Tiflux): lista com abas (polling 8s), detalhe,
 * mensagens incrementais via ?after_id (polling 4s) e estado do widget
 * público /public/chat/:token (polling 3s, também incremental).
 *
 * v8: com o stream SSE conectado (useRealtimeConnected), os pollings
 * autenticados caem para fallback lento (mensagens 4s→60s) — os eventos é que
 * invalidam as queries. O widget público não tem auth nem SSE: segue em 3s.
 */
import { useRef } from 'react';
import { queryOptions, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { isRealtimeConnected, useRealtimeConnected } from '@/lib/realtime';
import { TIMELINE_KEY } from '@/features/atendimentos/timeline-key';
import { MEDIA_AWAIT_POLL_MS, mergeMessagesById, oldestAwaitedMediaId } from './media-await';
import { whatsappTemplatesOfNumber } from '@erp/shared';
import type {
  ChatCloseReason,
  ChatDetail,
  ChatListItem,
  ChatMessage,
  ChatOrigin,
  ChatRelatedItem,
  ChatTab,
  PublicChatState,
} from '@erp/shared';

export const CHATS_KEY = ['erp', 'chats'] as const;

/** Prefixo de TODAS as variantes da lista de chats (abas/busca/origem). O
 *  badge da sidebar usa a mesma família (tab inbox) — invalidar este prefixo
 *  cobre lista E badge. Fonte canônica: o dispatcher realtime importa daqui. */
export const chatListsKey = [...CHATS_KEY, 'list'] as const;

/** Key do detalhe de um chat — fonte canônica, importada pelo dispatcher
 *  realtime. */
export const chatDetailKey = (chatId: number) => [...CHATS_KEY, 'detail', chatId] as const;

export interface ChatsListParams {
  /** 'all' = sem recorte de status (listas embutidas, v12) */
  tab: ChatTab;
  search?: string;
  origin?: ChatOrigin;
  /** filtro por departamento (?desk_id=, v8 Etapa 4) */
  deskId?: number;
  /** conversas de um cliente (?client_id=, v12) */
  clientId?: number;
  /** conversas vinculadas a um ticket (?ticket_number=, v12) */
  ticketNumber?: number;
}

/**
 * Toda variante da lista mora sob `chatListsKey` — inclusive os recortes
 * embutidos (cliente/ticket). É de propósito: o dispatcher realtime invalida o
 * PREFIXO em chat.updated, então as listas da aba "Conversas" do ticket
 * recebem as mesmas atualizações que a lista do /chats, sem key nova nem
 * handler novo.
 */
export function chatsListOptions(params: ChatsListParams) {
  const normalized = {
    tab: params.tab,
    search: params.search?.trim() || undefined,
    origin: params.origin,
    desk_id: params.deskId,
    client_id: params.clientId,
    ticket_number: params.ticketNumber,
  };
  return queryOptions({
    queryKey: [...chatListsKey, normalized] as const,
    queryFn: async ({ signal }) => {
      const res = await api<ChatListItem[]>('/chats', { query: { ...normalized }, signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export function chatDetailOptions(chatId: number) {
  return queryOptions({
    queryKey: chatDetailKey(chatId),
    queryFn: async ({ signal }) => {
      const res = await api<ChatDetail>(`/chats/${chatId}`, { signal });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/**
 * As outras conversas da MESMA pessoa (spec 028, FR-002): o telefone em outra
 * grafia, noutro número da empresa, ou uma conversa anterior. Sob CHATS_KEY —
 * as invalidações do chat a alcançam.
 */
export const chatRelatedKey = (chatId: number) => [...CHATS_KEY, 'related', chatId] as const;
export function chatRelatedOptions(chatId: number) {
  return queryOptions({
    queryKey: chatRelatedKey(chatId),
    queryFn: async ({ signal }) => {
      const res = await api<ChatRelatedItem[]>(`/chats/${chatId}/related`, { signal });
      if (res.error) throw res.error;
      return res.data;
    },
    // conversa nova da pessoa é rara; o foco da janela refaz
    staleTime: 30_000,
  });
}

/**
 * Motivos de encerramento do tenant (spec 013). `active` = a escolha do
 * diálogo de encerrar; sem ele, a lista inteira da configuração. Fora do
 * CHATS_KEY de propósito: invalidar a lista de conversas não refaz os motivos.
 */
export const closeReasonsKey = ['erp', 'chat-close-reasons'] as const;
export function closeReasonsOptions(opts: { activeOnly?: boolean } = {}) {
  return queryOptions({
    queryKey: [...closeReasonsKey, opts.activeOnly ? 'active' : 'all'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<ChatCloseReason[]>('/chat/close-reasons', {
        query: opts.activeOnly ? { active: 1 } : {},
        signal,
      });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export const chatMessagesKey = (chatId: number) => ['erp', 'chats', 'messages', chatId] as const;

/**
 * Janela mínima entre dois refetches COMPLETOS (sem after_id) para
 * ressincronizar os ticks de entrega do WhatsApp. Com o polling de 4s, o
 * resync efetivo acontece a cada ~12s (o primeiro poll após a janela vencer)
 * — os demais polls seguem incrementais.
 */
const TICK_SYNC_WINDOW_MS = 10_000;

/**
 * Chats marcados para o PRÓXIMO fetch de mensagens ser completo (sem
 * after_id), mesclando por id. Setado pelo dispatcher de eventos realtime em
 * chat.message.updated (tick/mídia mudou campo de mensagem já cacheada — o
 * caminho incremental nunca veria); consumido pelo queryFn de useChatMessages.
 */
const fullResyncRequested = new Set<number>();
export function requestChatFullResync(chatId: number): void {
  fullResyncRequested.add(chatId);
}

/**
 * "Um campo de mensagem desta conversa mudou" (tick de entrega, cópia da
 * mídia): o próximo fetch das mensagens vem COMPLETO e mescla por id, e a
 * linha do tempo do atendimento refaz. É a reação ao evento
 * chat.message.updated — e o que o "tentar de novo" da mídia indisponível faz
 * à mão quando o evento não veio. Resolve quando os refetches das queries
 * montadas terminam (as desmontadas só ficam stale).
 */
export async function resyncChatMessages(qc: QueryClient, chatId: number): Promise<void> {
  requestChatFullResync(chatId);
  await Promise.all([
    qc.invalidateQueries({ queryKey: chatMessagesKey(chatId) }),
    qc.invalidateQueries({ queryKey: TIMELINE_KEY }),
  ]);
}

/** Ack ainda em progresso (sent < delivered < read; read/failed são terminais). */
function isTickPending(m: ChatMessage): boolean {
  return (
    m.direction === 'outbound' &&
    !m.internal &&
    m.authorType !== 'system' &&
    // spec 028: a fala do bot saiu pelo runtime, não pelo ERP — nunca ganha ack aqui
    m.authorType !== 'bot' &&
    (m.deliveryStatus === null || m.deliveryStatus === 'sent' || m.deliveryStatus === 'delivered')
  );
}

/**
 * Mensagens do chat com append incremental: cada refetch envia
 * ?after_id=<última id conhecida> e concatena só as novas (dedupe por id).
 *
 * Resync de ticks (v6): o caminho incremental só APPENDA — o deliveryStatus /
 * wamid de mensagens já cacheadas nunca mudaria. Quando o chat é whatsapp E há
 * no cache outbound com ack pendente, um refetch completo (sem after_id) roda
 * no máximo a cada TICK_SYNC_WINDOW_MS e mescla por id, ATUALIZANDO os campos
 * das mensagens existentes. Chats não-whatsapp ou sem pendência mantêm o
 * comportamento incremental intacto (custo zero).
 *
 * Mídia esperando a cópia (media_pull, ver media-await): enquanto houver
 * mensagem de mídia sem mediaUrl dentro do teto, o polling cai para
 * MEDIA_AWAIT_POLL_MS e cada fetch pede a JANELA a partir da mais antiga
 * delas (after_id = id − 1), mesclando por id — é assim que o mediaUrl que
 * chegou aparece sem depender do evento realtime. Vencido o teto, o polling
 * volta ao normal e a bolha oferece "tentar de novo".
 */
export function useChatMessages(chatId: number, origin?: ChatOrigin) {
  const qc = useQueryClient();
  const queryKey = chatMessagesKey(chatId);
  // Momento do último fetch completo — reinicia por chat (o componente da
  // conversa é montado com key={chatId}).
  const lastFullSyncRef = useRef(0);
  // Com SSE conectado o polling vira fallback lento; mudar `connected`
  // re-renderiza (useSyncExternalStore) e o useQuery reagenda o timer.
  const connected = useRealtimeConnected();
  return useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const prev = qc.getQueryData<ChatMessage[]>(queryKey);
      // Pedido explícito de resync (evento chat.message.updated) OU, só com o
      // stream FORA, a janela periódica de resync de ticks do WhatsApp — com
      // SSE conectado os ticks chegam por evento e a janela fica desligada.
      const forceFull = fullResyncRequested.delete(chatId);
      const needsTickSync =
        forceFull ||
        (!isRealtimeConnected() &&
          origin === 'whatsapp' &&
          prev !== undefined &&
          Date.now() - lastFullSyncRef.current >= TICK_SYNC_WINDOW_MS &&
          prev.some(isTickPending));
      // sem fetch completo, a mídia que espera a cópia puxa a janela dela
      const awaitedFrom =
        needsTickSync || prev === undefined ? null : oldestAwaitedMediaId(prev, Date.now());
      const afterId = needsTickSync
        ? undefined
        : awaitedFrom !== null
          ? awaitedFrom - 1
          : prev?.at(-1)?.id;
      const res = await api<ChatMessage[]>(`/chats/${chatId}/messages`, {
        query: { after_id: afterId },
        signal,
      });
      if (res.error) {
        // Não perde o pedido de resync se o fetch falhou.
        if (forceFull) fullResyncRequested.add(chatId);
        throw res.error;
      }
      if (!prev) {
        lastFullSyncRef.current = Date.now();
        return res.data;
      }
      if (needsTickSync) {
        lastFullSyncRef.current = Date.now();
        // fetch completo: as conhecidas voltam frescas (ticks/wamid/mídia)
        return mergeMessagesById(prev, res.data);
      }
      // a janela da mídia também repete mensagens já conhecidas
      if (awaitedFrom !== null) return mergeMessagesById(prev, res.data);
      if (res.data.length === 0) return prev;
      const known = new Set(prev.map((m) => m.id));
      return [...prev, ...res.data.filter((m) => !known.has(m.id))];
    },
    // reavaliado a cada resposta: mídia esperando dentro do teto ⇒ curto;
    // senão o de sempre (fallback lento com SSE conectado)
    refetchInterval: (query) =>
      oldestAwaitedMediaId(query.state.data ?? [], Date.now()) !== null
        ? MEDIA_AWAIT_POLL_MS
        : connected
          ? 60_000
          : 4_000,
  });
}

// ── WhatsApp: modelos HSM e janela de 24h (v8 Etapa 3b) ─────────────────────

/** Modelo HSM aprovado na Meta: body com placeholders {{1}}..{{n}}. */
export interface WhatsappHsmTemplate {
  name: string;
  language: string;
  category: string;
  body: string;
  /** quantidade de parâmetros ({{1}}..{{n}}) */
  params: number;
  /**
   * os números (phone_number_id) que têm o modelo: o canal escolhido mostra
   * só os dele — o número da Blip tem os da conta da Blip (spec 025). null =
   * a API ainda não manda a informação (o web pode publicar antes dela): o
   * modelo vale para qualquer número, como antes.
   */
  phoneNumberIds: string[] | null;
}

/** um número cujos modelos não carregaram (o resto da lista segue) */
export interface WhatsappHsmTemplateFailure {
  phoneNumberId: string;
  provider: 'meta' | 'blip';
  error: string;
}

export interface WhatsappHsmTemplateList {
  templates: WhatsappHsmTemplate[];
  failures: WhatsappHsmTemplateFailure[];
}

/** os modelos de UM número (null = sem filtro: a lista do tenant inteiro) —
 *  a regra é a do @erp/shared, provada nos testes da API */
export function templatesOfNumber(
  list: WhatsappHsmTemplateList | undefined,
  phoneNumberId: string | null
): { templates: WhatsappHsmTemplate[]; failures: WhatsappHsmTemplateFailure[] } {
  return whatsappTemplatesOfNumber(list, phoneNumberId);
}

/**
 * Por que os modelos de um número não vieram, em pt-BR (o código fica no fim,
 * para quem for investigar).
 */
export function templateFailureMessage(f: WhatsappHsmTemplateFailure): string {
  const source = f.provider === 'blip' ? 'da Blip' : 'da Meta';
  const reason =
    f.error === 'not_blip_number'
      ? 'o broker não reconhece este número como da Blip (configuração)'
      : f.error === 'unauthorized'
        ? 'a chave da integração foi recusada'
        : f.error === 'blip_templates_failed'
          ? 'a Blip não respondeu'
          : f.error === 'TimeoutError'
            ? 'o serviço demorou demais para responder'
            : 'o serviço não respondeu';
  return `Os modelos ${source} deste número não carregaram: ${reason} (${f.error}).`;
}

export function whatsappTemplatesOptions() {
  return queryOptions({
    queryKey: [...CHATS_KEY, 'wa-templates'] as const,
    queryFn: async ({ signal }): Promise<WhatsappHsmTemplateList> => {
      const res = await api<Partial<WhatsappHsmTemplateList>>('/whatsapp/templates', {
        signal,
      });
      if (res.error) throw res.error;
      return {
        templates: (res.data.templates ?? []).map((t) => ({
          ...t,
          phoneNumberIds: Array.isArray(t.phoneNumberIds) ? t.phoneNumberIds : null,
        })),
        failures: res.data.failures ?? [],
      };
    },
    // Modelos mudam raramente (aprovação na Meta) — cache generoso.
    staleTime: 5 * 60_000,
  });
}

export const whatsappWindowKey = (chatId: number) =>
  [...CHATS_KEY, 'wa-window', chatId] as const;

/**
 * Janela de 24h do WhatsApp: fora dela só é permitido enviar modelo HSM. O
 * refetch periódico detecta a janela expirando com o chat aberto; o envio de
 * modelo invalida a key (useSendChatTemplate).
 */
export function whatsappWindowOptions(chatId: number) {
  return queryOptions({
    queryKey: whatsappWindowKey(chatId),
    queryFn: async ({ signal }) => {
      const res = await api<{ open: boolean }>('/whatsapp/window', {
        query: { chatId },
        signal,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

// ── Widget público (sem auth; /api/v1/public/chat) ──────────────────────────

export const publicChatKey = (token: string) => ['erp', 'public-chat', token] as const;

/**
 * Estado do widget: status/responsável sempre frescos; mensagens acumuladas
 * incrementalmente (?after_id) do lado do cliente.
 */
export function usePublicChatState(token: string | null) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ['erp', 'public-chat', token ?? ''] as const,
    enabled: token !== null,
    retry: false,
    refetchInterval: 3_000,
    queryFn: async ({ signal }) => {
      const key = publicChatKey(token ?? '');
      const prev = qc.getQueryData<PublicChatState>(key);
      const afterId = prev?.messages.at(-1)?.id;
      const res = await api<PublicChatState>(`/public/chat/${token}`, {
        query: { after_id: afterId },
        signal,
      });
      if (res.error) throw res.error;
      if (!prev) return res.data;
      if (res.data.messages.length === 0) return { ...res.data, messages: prev.messages };
      const known = new Set(prev.messages.map((m) => m.id));
      return {
        ...res.data,
        messages: [...prev.messages, ...res.data.messages.filter((m) => !known.has(m.id))],
      };
    },
  });
}
